import "./Select.css";
import cn from "classnames";
import {
	type ChangeEvent,
	Children,
	type CSSProperties,
	type FC,
	type FocusEvent,
	isValidElement,
	type KeyboardEvent,
	type ReactElement,
	type ReactNode,
	useCallback,
	useEffect,
	useId,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { Icon } from "../common/Icon";

type SelectItem =
	| { kind: "group"; key: string; label: string }
	| {
			kind: "option";
			key: string;
			value: string;
			label: ReactNode;
			disabled: boolean;
	  };

export type SelectProps = {
	children?: ReactNode;
	trigger?: ReactNode;
	className?: string;
	id?: string;
	name?: string;
	value?: string | number | readonly string[];
	defaultValue?: string | number | readonly string[];
	disabled?: boolean;
	required?: boolean;
	isInvalid?: boolean;
	size?: "sm" | "lg";
	title?: string;
	style?: CSSProperties;
	autoFocus?: boolean;
	placeholder?: string;
	multiple?: boolean;
	onChange?: (event: ChangeEvent<HTMLSelectElement>) => void;
	onBlur?: (event: FocusEvent<HTMLSelectElement>) => void;
	"aria-label"?: string;
	"aria-labelledby"?: string;
	"aria-busy"?: boolean | "true" | "false";
};

function nodeText(node: ReactNode): string {
	if (node == null || typeof node === "boolean") {
		return "";
	}
	if (typeof node === "string" || typeof node === "number") {
		return String(node);
	}
	if (Array.isArray(node)) {
		return node.map(nodeText).join("");
	}
	if (isValidElement<{ children?: ReactNode }>(node)) {
		return nodeText(node.props.children);
	}
	return "";
}

function parseItems(children: ReactNode, prefix = "opt"): SelectItem[] {
	const items: SelectItem[] = [];

	Children.forEach(children, (child, index) => {
		if (child == null || typeof child === "boolean") {
			return;
		}
		if (Array.isArray(child)) {
			items.push(...parseItems(child, `${prefix}-${index}`));
			return;
		}
		if (!isValidElement(child)) {
			return;
		}

		const element = child as ReactElement<{
			children?: ReactNode;
			label?: string;
			value?: string | number;
			disabled?: boolean;
		}>;

		if (element.type === "optgroup") {
			const groupKey = `${prefix}-g${index}`;
			items.push({
				kind: "group",
				key: groupKey,
				label: element.props.label ?? "",
			});
			items.push(...parseItems(element.props.children, groupKey));
			return;
		}

		if (element.type !== "option") {
			if (element.props.children) {
				items.push(...parseItems(element.props.children, `${prefix}-${index}`));
			}
			return;
		}

		const label = element.props.children;
		const rawValue = element.props.value;
		items.push({
			kind: "option",
			key: `${prefix}-${index}`,
			value: rawValue === undefined ? nodeText(label) : String(rawValue),
			label,
			disabled: Boolean(element.props.disabled),
		});
	});

	return items;
}

function toValueString(
	value: SelectProps["value"] | SelectProps["defaultValue"],
) {
	if (value == null) {
		return "";
	}
	if (Array.isArray(value)) {
		return String(value[0] ?? "");
	}
	return String(value);
}

export const Select: FC<SelectProps> = ({
	children,
	trigger,
	className,
	id,
	name,
	value,
	defaultValue,
	disabled = false,
	isInvalid,
	size,
	title,
	style,
	placeholder,
	multiple = false,
	onChange,
	onBlur,
	"aria-label": ariaLabel,
	"aria-labelledby": ariaLabelledBy,
	"aria-busy": ariaBusy,
}) => {
	const generatedId = useId();
	const listboxId = `${id ?? generatedId}-listbox`;
	const triggerRef = useRef<HTMLButtonElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);
	const typeaheadRef = useRef("");
	const typeaheadTimer = useRef<number>(0);

	const isControlled = value !== undefined;
	const [uncontrolledValue, setUncontrolledValue] = useState(
		toValueString(defaultValue),
	);
	const currentValue = isControlled ? toValueString(value) : uncontrolledValue;

	const classTokens = className?.split(/\s+/).filter(Boolean) ?? [];
	const compact = size === "sm" || classTokens.includes("form-select-sm");
	const large = size === "lg" || classTokens.includes("form-select-lg");
	const extraClassName = classTokens
		.filter(
			(token) =>
				token !== "form-select" &&
				token !== "form-select-sm" &&
				token !== "form-select-lg",
		)
		.join(" ");

	const items = useMemo(() => parseItems(children), [children]);
	const options = useMemo(
		() => items.filter((item) => item.kind === "option"),
		[items],
	);
	const selected = options.find((option) => option.value === currentValue);
	const selectedLabel = selected?.label ?? placeholder ?? "";
	const isPlaceholder =
		!selected ||
		(selected.disabled && selected.value === "" && currentValue === "");

	const [open, setOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(0);
	const [menuStyle, setMenuStyle] = useState<CSSProperties>();

	const enabledIndexes = useMemo(
		() =>
			options
				.map((option, index) => (option.disabled ? -1 : index))
				.filter((index) => index >= 0),
		[options],
	);

	const updateMenuPosition = useCallback(() => {
		const triggerEl = triggerRef.current;
		if (!triggerEl) {
			return;
		}

		const rect = triggerEl.getBoundingClientRect();
		const viewportPadding = 8;
		const gap = 6;
		const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
		const spaceAbove = rect.top - viewportPadding;
		const maxHeight = Math.min(280, Math.max(spaceBelow, spaceAbove) - gap);
		const openUp = spaceBelow < 160 && spaceAbove > spaceBelow;
		const width = Math.max(rect.width, compact ? 160 : 198);

		setMenuStyle({
			position: "fixed",
			left: Math.min(rect.left, window.innerWidth - width - viewportPadding),
			width,
			maxHeight: Math.max(maxHeight, 120),
			top: openUp ? undefined : rect.bottom + gap,
			bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
		});
	}, [compact]);

	const emitChange = useCallback(
		(next: string) => {
			if (!isControlled) {
				setUncontrolledValue(next);
			}
			onChange?.({
				target: {
					value: next,
					name: name ?? "",
					type: "select-one",
				},
				currentTarget: {
					value: next,
					name: name ?? "",
					type: "select-one",
				},
			} as ChangeEvent<HTMLSelectElement>);
		},
		[isControlled, name, onChange],
	);

	const emitBlur = useCallback(() => {
		onBlur?.({
			target: { name: name ?? "", value: currentValue },
			currentTarget: { name: name ?? "", value: currentValue },
		} as FocusEvent<HTMLSelectElement>);
	}, [currentValue, name, onBlur]);

	const close = useCallback(() => {
		setOpen(false);
	}, []);

	const selectIndex = useCallback(
		(index: number) => {
			const option = options[index];
			if (!option || option.disabled) {
				return;
			}
			emitChange(option.value);
			setOpen(false);
			triggerRef.current?.focus();
		},
		[emitChange, options],
	);

	useLayoutEffect(() => {
		if (!open) {
			return;
		}
		updateMenuPosition();
		const selectedIndex = Math.max(
			0,
			options.findIndex((option) => option.value === currentValue),
		);
		setActiveIndex(
			options[selectedIndex]?.disabled
				? (enabledIndexes[0] ?? 0)
				: selectedIndex,
		);
	}, [open, updateMenuPosition, options, currentValue, enabledIndexes]);

	useEffect(() => {
		if (!open) {
			return;
		}

		const onPointer = (event: MouseEvent) => {
			const target = event.target as Node;
			if (
				triggerRef.current?.contains(target) ||
				menuRef.current?.contains(target)
			) {
				return;
			}
			close();
			emitBlur();
		};

		const onReposition = () => updateMenuPosition();

		document.addEventListener("mousedown", onPointer);
		window.addEventListener("resize", onReposition);
		window.addEventListener("scroll", onReposition, true);
		return () => {
			document.removeEventListener("mousedown", onPointer);
			window.removeEventListener("resize", onReposition);
			window.removeEventListener("scroll", onReposition, true);
		};
	}, [open, close, emitBlur, updateMenuPosition]);

	useEffect(() => {
		return () => window.clearTimeout(typeaheadTimer.current);
	}, []);

	const moveActive = (direction: 1 | -1) => {
		if (enabledIndexes.length === 0) {
			return;
		}
		const currentPos = enabledIndexes.indexOf(activeIndex);
		const nextPos =
			currentPos === -1
				? direction === 1
					? 0
					: enabledIndexes.length - 1
				: (currentPos + direction + enabledIndexes.length) %
					enabledIndexes.length;
		setActiveIndex(enabledIndexes[nextPos]);
	};

	const handleTypeahead = (key: string) => {
		typeaheadRef.current += key.toLowerCase();
		window.clearTimeout(typeaheadTimer.current);
		typeaheadTimer.current = window.setTimeout(() => {
			typeaheadRef.current = "";
		}, 500);

		const query = typeaheadRef.current;
		const start = open ? activeIndex + 1 : 0;
		const pool = [...options.slice(start), ...options.slice(0, start)];
		const match = pool.find(
			(option) =>
				!option.disabled &&
				nodeText(option.label).toLowerCase().startsWith(query),
		);
		if (!match) {
			return;
		}
		const matchIndex = options.indexOf(match);
		if (!open) {
			setOpen(true);
		}
		setActiveIndex(matchIndex);
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
		if (disabled) {
			return;
		}

		switch (event.key) {
			case "ArrowDown":
				event.preventDefault();
				if (!open) {
					setOpen(true);
				} else {
					moveActive(1);
				}
				break;
			case "ArrowUp":
				event.preventDefault();
				if (!open) {
					setOpen(true);
				} else {
					moveActive(-1);
				}
				break;
			case "Home":
				if (open && enabledIndexes.length) {
					event.preventDefault();
					setActiveIndex(enabledIndexes[0]);
				}
				break;
			case "End":
				if (open && enabledIndexes.length) {
					event.preventDefault();
					setActiveIndex(enabledIndexes[enabledIndexes.length - 1]);
				}
				break;
			case "Enter":
			case " ":
				event.preventDefault();
				if (!open) {
					setOpen(true);
				} else {
					selectIndex(activeIndex);
				}
				break;
			case "Escape":
				if (open) {
					event.preventDefault();
					close();
				}
				break;
			case "Tab":
				if (open) {
					close();
				}
				break;
			default:
				if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
					handleTypeahead(event.key);
				}
		}
	};

	let optionIndex = -1;

	if (multiple) {
		return (
			<select
				id={id}
				name={name}
				multiple
				className={cn("moxie-select-multiple", extraClassName, {
					"is-invalid": isInvalid,
					"moxie-select-sm": compact,
					"moxie-select-lg": large,
				})}
				value={value as string[] | undefined}
				defaultValue={
					isControlled ? undefined : (defaultValue as string[] | undefined)
				}
				disabled={disabled}
				title={title}
				style={style}
				aria-label={ariaLabel}
				aria-labelledby={ariaLabelledBy}
				aria-busy={ariaBusy}
				onChange={onChange}
				onBlur={onBlur}
			>
				{children}
			</select>
		);
	}

	return (
		<div
			className={cn("moxie-select", extraClassName, {
				"is-open": open,
				"is-invalid": isInvalid,
				"moxie-select-sm": compact,
				"moxie-select-lg": large,
			})}
			style={style}
		>
			{/* biome-ignore lint/a11y/useSemanticElements: native select menus cannot match the Moxie UI */}
			<button
				ref={triggerRef}
				type="button"
				id={id}
				className="moxie-select-trigger"
				disabled={disabled}
				title={title}
				aria-label={ariaLabel}
				aria-labelledby={ariaLabelledBy}
				aria-busy={ariaBusy}
				role="combobox"
				aria-haspopup="listbox"
				aria-expanded={open}
				aria-controls={listboxId}
				aria-invalid={isInvalid || undefined}
				aria-activedescendant={
					open && options[activeIndex]
						? `${listboxId}-opt-${activeIndex}`
						: undefined
				}
				onClick={() => {
					if (disabled) {
						return;
					}
					setOpen((prev) => !prev);
				}}
				onBlur={() => {
					if (!open) {
						emitBlur();
					}
				}}
				onKeyDown={handleKeyDown}
			>
				<span
					className={cn("moxie-select-value", {
						"is-placeholder": !trigger && isPlaceholder,
					})}
				>
					{trigger ?? selectedLabel}
				</span>
				<Icon
					name="keyboard_arrow_down"
					className="moxie-select-chevron"
					size={compact ? 18 : 22}
				/>
			</button>

			{open &&
				createPortal(
					// biome-ignore lint/a11y/useSemanticElements: native select menus cannot match the Moxie UI
					<div
						ref={menuRef}
						id={listboxId}
						role="listbox"
						className="moxie-select-menu"
						style={menuStyle}
					>
						{items.map((item) => {
							if (item.kind === "group") {
								return (
									<div key={item.key} className="moxie-select-group">
										{item.label}
									</div>
								);
							}

							optionIndex += 1;
							const index = optionIndex;
							const isSelected = item.value === currentValue;
							return (
								// biome-ignore lint/a11y/useSemanticElements: custom listbox option
								<button
									key={item.key}
									type="button"
									id={`${listboxId}-opt-${index}`}
									role="option"
									aria-selected={isSelected}
									disabled={item.disabled}
									className={cn("moxie-select-option", {
										"is-selected": isSelected,
										"is-active": index === activeIndex,
									})}
									onMouseDown={(event) => event.preventDefault()}
									onMouseEnter={() => {
										if (!item.disabled) {
											setActiveIndex(index);
										}
									}}
									onClick={() => selectIndex(index)}
								>
									<span className="moxie-select-option-label">
										{item.label}
									</span>
									{isSelected && (
										<Icon
											name="check"
											className="moxie-select-check"
											size={16}
										/>
									)}
								</button>
							);
						})}
					</div>,
					document.body,
				)}
		</div>
	);
};
